import * as ActionTypes from '../actions/actionTypes';
import {DATA_SETS} from '../Constants';
import {
  calculateTableData,
  convertToGraphData,
  tableMetaData
} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {
  calculateCC,
  calculateDFMC,
  calculateDIR,
  calculateEO_FH,
  calculateEO_FI,
  calculateEO_FROS,
  calculateEO_GROS,
  calculateEO_GROS_SLP,
  calculateEO_ROS_HW,
  calculateFDI,
  calculateFMC,
  calculateFMC1,
  calculateFMC2,
  calculateFMC3,
  calculateGZD_FH,
  calculateGZD_FI,
  calculateGZD_FROS,
  calculateGZD_GROS,
  calculateGZD_GROS_SLP,
  calculateGZD_ROS_HW,
  calculateGZD_ROS_LW,
  calculateGFDI,
  calculateNAT_FH,
  calculateNAT_FI,
  calculateNAT_FROS,
  calculateNAT_GROS,
  calculateNAT_GROS_SLP,
  calculateNAT_ROS_HW,
  calculateNAT_ROS_LW
} from '../utils/behaviour_models/grass/calculations';
import {
  DEFAULT_SLOPE,
  GFDI_COLORS,
  GRASS_KEY_EO_GROS,
  GRASS_KEY_GZD_GROS,
  GRASS_KEY_NAT_GROS
} from '../utils/behaviour_models/grass/constants';
import {runParityCalcsFromConfig} from '../utils/behaviour_models/shared/parity';
import {emptyGraphMap, extractTicks} from '../utils/point_utils';

export {
  GRASS_KEY_NAT_GROS,
  GRASS_KEY_GZD_GROS,
  GRASS_KEY_EO_GROS,
  GFDI_COLORS,
  calculateGFDI
};

const extractVariables = (state) => {
  return {
    slope: state.slope
  };
};

const VALUES_CONFIG = [
  { key: 'DIR', displayName: 'Direction of Spread', calc: calculateDIR, decPlaces: 0, arrow: 'fa-arrow-up' },
  { key: 'FDI', displayName: 'Grass FDI (Noble eqn.)', calc: calculateFDI, decPlaces: 1, colors: GFDI_COLORS },
  { key: 'DFMC', displayName: 'Dead FMC (%)', calc: calculateDFMC, decPlaces: 1 },
  { key: 'FMC_1', displayName: 'FM Coefficient - Moisture < 12%', calc: calculateFMC1, decPlaces: 3, nonFinal: true },
  { key: 'FMC_2', displayName: 'FM Coefficient - Wind < 10', calc: calculateFMC2, decPlaces: 3, nonFinal: true },
  { key: 'FMC_3', displayName: 'FM Coefficient - Wind > 10', calc: calculateFMC3, decPlaces: 3, nonFinal: true },
  { key: 'FMC', displayName: 'Fuel Moisture Coefficient', calc: calculateFMC, decPlaces: 3 },
  { key: 'CC', displayName: 'Curing Coefficient', calc: calculateCC, decPlaces: 2 },
  {
    key: 'NAT_ROS_LW',
    displayName: 'Natural ROS - wind < 5 (km/h)',
    calc: calculateNAT_ROS_LW,
    decPlaces: 2,
    nonFinal: true
  },
  {
    key: 'NAT_ROS_HW',
    displayName: 'Natural ROS - wind > 5 (km/h)',
    calc: calculateNAT_ROS_HW,
    decPlaces: 2,
    nonFinal: true
  },
  {
    key: 'GZD_ROS_LW',
    displayName: 'Grazed ROS - wind < 5 (km/h)',
    calc: calculateGZD_ROS_LW,
    decPlaces: 2,
    nonFinal: true
  },
  {
    key: 'GZD_ROS_HW',
    displayName: 'Grazed ROS - wind > 5 (km/h)',
    calc: calculateGZD_ROS_HW,
    decPlaces: 2,
    nonFinal: true
  },
  {
    key: 'EO_ROS_HW',
    displayName: 'Eaten Out ROS - wind > 5 (km/h)',
    calc: calculateEO_ROS_HW,
    decPlaces: 2,
    nonFinal: true
  },
  {
    key: GRASS_KEY_NAT_GROS, displayName: 'Natural Grass ROS (m/h)', calc: calculateNAT_GROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  { key: 'NAT_GROS_SLP', displayName: 'Natural Grass ROS slope (m/h)', calc: calculateNAT_GROS_SLP, decPlaces: 0 },
  {
    key: 'NAT_FROS', displayName: 'Natural Flank ROS (m/h)', calc: calculateNAT_FROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'NAT_FH', displayName: 'Natural Grass Flame Ht (m)', calc: calculateNAT_FH, decPlaces: 1,
    colors: {
      scale: {
        '1.5': 'rgba(122, 193, 66, 0.7)',
        '4': 'rgba(248, 152, 41, 0.7)'
      },
      else: 'rgba(238, 46, 36, 0.7)'
    }
  },
  {
    key: 'NAT_FI', displayName: 'Natural Fireline Intensity (kW/m)', calc: calculateNAT_FI, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 15000.0,
      leftColor: 'rgba(248, 152, 41, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: GRASS_KEY_GZD_GROS, displayName: 'Grazed Grass ROS (m/h)', calc: calculateGZD_GROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'GZD_GROS_SLP', displayName: 'Grazed Grass ROS slope (m/h)', calc: calculateGZD_GROS_SLP, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'GZD_FROS', displayName: 'Grazed Flank ROS (m/h)', calc: calculateGZD_FROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'GZD_FH', displayName: 'Grazed Grass Flame Ht (m)', calc: calculateGZD_FH, decPlaces: 1,
    colors: {
      scale: {
        '1.5': 'rgba(122, 193, 66, 0.7)',
        '4': 'rgba(248, 152, 41, 0.7)'
      },
      else: 'rgba(238, 46, 36, 0.7)'
    }
  },
  {
    key: 'GZD_FI', displayName: 'Grazed Fireline Intensity (kW/m)', calc: calculateGZD_FI, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 15000.0,
      leftColor: 'rgba(248, 152, 41, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: GRASS_KEY_EO_GROS, displayName: 'Eaten-Out Grass ROS (m/h)', calc: calculateEO_GROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'EO_GROS_SLP', displayName: 'Eaten-Out Grass ROS slope (m/h)', calc: calculateEO_GROS_SLP, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'EO_FROS', displayName: 'Eaten-Out Flank ROS (m/h)', calc: calculateEO_FROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 5000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'EO_FH', displayName: 'Eaten-Out Grass Flame Ht (m)', calc: calculateEO_FH, decPlaces: 1,
    colors: {
      scale: {
        '1.5': 'rgba(122, 193, 66, 0.7)',
        '4': 'rgba(248, 152, 41, 0.7)'
      },
      else: 'rgba(238, 46, 36, 0.7)'
    }
  },
  {
    key: 'EO_FI', displayName: 'Eaten-Out Fireline Intensity (kW/m)', calc: calculateEO_FI, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 15000.0,
      leftColor: 'rgba(248, 152, 41, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  }
];

const GRAPHS_CONFIG = [
  {
    title: 'Natual Rate of Spread',
    id: 'NROS',
    type: 'LineChart',
    YAxisLabel: 'km/h',
    series: [
      {
        name: 'Wind < 5',
        dataKey: 'NAT_ROS_LW',
        type: 'monotone',
        stroke: '#008CC4',
        active: true
      },
      {
        name: 'Wind > 5',
        dataKey: 'NAT_ROS_HW',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      }
    ]
  },
  {
    title: 'Grazed Rate of Spread',
    id: 'GROS',
    type: 'LineChart',
    YAxisLabel: 'km/h',
    series: [
      {
        name: 'Wind < 5',
        dataKey: 'GZD_ROS_LW',
        type: 'monotone',
        stroke: '#008CC4',
        active: true
      },
      {
        name: 'Wind > 5',
        dataKey: 'GZD_ROS_HW',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      }
    ]
  },
  {
    title: 'Eaten Out Rate of Spread',
    id: 'EO_ROS',
    type: 'LineChart',
    YAxisLabel: 'km/h',
    series: [
      {
        name: 'Wind > 5',
        dataKey: 'EO_ROS_HW',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      }
    ]
  }
];

const initialState = {
  slope: DEFAULT_SLOPE,
  table_data: {},
  graph_data: emptyGraphMap(GRAPHS_CONFIG),
  table_meta_data: tableMetaData(VALUES_CONFIG, DATA_SETS.GRASS),
  graph_meta_data: GRAPHS_CONFIG
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_BEHAVIOUR_VARIABLES:
      if (action.data_set === DATA_SETS.GRASS) {
        return {
          ...state,
          ...action.variables
        };
      }
      break;
    case ActionTypes.CALCULATE_GRASS:
      const variables = extractVariables(state);
      const modifications = action.modifications ? action.modifications.properties.data : {};
      const new_table_data =
        calculateTableData(VALUES_CONFIG,
          action.include_history,
          action.short_future,
          action.weather_data.table_data,
          variables,
          modifications);
      const new_graph_data = convertToGraphData(GRAPHS_CONFIG, new_table_data);
      const ticks = extractTicks(new_graph_data, GRAPHS_CONFIG[0].id);

      return {
        ...state,
        ticks,
        graph_data: new_graph_data,
        table_data: new_table_data
      };

    default:
      return state;
  }
  return state;
};

export const runParityCalcs = (wd, variables) => runParityCalcsFromConfig(VALUES_CONFIG, wd, variables);

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/point_data_grass.js